import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface CameraDevice {
  _id: string;
  name: string;
  deviceType: 'smartphone' | 'tablet' | 'security-camera' | 'action-camera';
  userId: string;
  userName: string;
  userRole: string;
  status: 'online' | 'offline' | 'recording' | 'standby' | 'error';
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  batteryLevel: number;
  storageUsed: number;
  storageTotal: number;
  connectionType: 'wifi' | 'cellular' | 'offline';
  signalStrength: number;
  resolution: string;
  quality: 'low' | 'medium' | 'high' | 'ultra';
  isRecording: boolean;
  recordingStartTime?: string;
  autoUpload: boolean;
  encryptionEnabled: boolean;
  lastSync: string;
  totalPhotos: number;
  totalVideos: number;
  workSite?: string;
  permissions: {
    canRecord: boolean;
    canUpload: boolean;
    canDelete: boolean;
    canShare: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CameraSettings {
  _id: string;
  deviceId: string;
  resolution: string;
  quality: 'low' | 'medium' | 'high' | 'ultra';
  autoUpload: boolean;
  uploadOnWifiOnly: boolean;
  encryptionEnabled: boolean;
  geotagging: boolean;
  timestamping: boolean;
  watermark: boolean;
  compressionLevel: number;
  maxFileSize: number;
  recordingTimeLimit: number;
  storageCleanupEnabled: boolean;
  storageCleanupDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface MediaFile {
  _id: string;
  deviceId: string;
  filename: string;
  originalName: string;
  type: 'photo' | 'video';
  size: number;
  duration?: number;
  resolution: string;
  quality: string;
  location?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  timestamp: string;
  uploaded: boolean;
  uploadedAt?: string;
  thumbnailUrl?: string;
  url: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CameraFilters {
  status?: string;
  deviceType?: string;
  userId?: string;
  workSite?: string;
  connectionType?: string;
}

export interface CameraStats {
  totalDevices: number;
  onlineDevices: number;
  recordingDevices: number;
  totalPhotos: number;
  totalVideos: number;
  totalStorage: number;
  usedStorage: number;
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Camera Management Service
class CameraManagementService {
  // Device Management
  async getDevices(filters?: CameraFilters): Promise<CameraDevice[]> {
    const response = await api.get('/cameras/devices', { params: filters });
    return response.data;
  }

  async getDevice(id: string): Promise<CameraDevice> {
    const response = await api.get(`/cameras/devices/${id}`);
    return response.data;
  }

  async createDevice(device: Omit<CameraDevice, '_id' | 'createdAt' | 'updatedAt'>): Promise<CameraDevice> {
    const response = await api.post('/cameras/devices', device);
    return response.data;
  }

  async updateDevice(id: string, device: Partial<CameraDevice>): Promise<CameraDevice> {
    const response = await api.put(`/cameras/devices/${id}`, device);
    return response.data;
  }

  async deleteDevice(id: string): Promise<void> {
    await api.delete(`/cameras/devices/${id}`);
  }

  // Device Control
  async startRecording(deviceId: string): Promise<void> {
    await api.post(`/cameras/devices/${deviceId}/start-recording`);
  }

  async stopRecording(deviceId: string): Promise<void> {
    await api.post(`/cameras/devices/${deviceId}/stop-recording`);
  }

  async syncDevice(deviceId: string): Promise<void> {
    await api.post(`/cameras/devices/${deviceId}/sync`);
  }

  // Settings Management
  async getDeviceSettings(deviceId: string): Promise<CameraSettings> {
    const response = await api.get(`/cameras/devices/${deviceId}/settings`);
    return response.data;
  }

  async updateDeviceSettings(deviceId: string, settings: Partial<CameraSettings>): Promise<CameraSettings> {
    const response = await api.put(`/cameras/devices/${deviceId}/settings`, settings);
    return response.data;
  }

  // Media Management
  async getMediaFiles(deviceId?: string, type?: 'photo' | 'video'): Promise<MediaFile[]> {
    const params: any = {};
    if (deviceId) params.deviceId = deviceId;
    if (type) params.type = type;
    
    const response = await api.get('/cameras/media', { params });
    return response.data;
  }

  async getMediaFile(id: string): Promise<MediaFile> {
    const response = await api.get(`/cameras/media/${id}`);
    return response.data;
  }

  async uploadMediaFile(deviceId: string, file: File, metadata: any): Promise<MediaFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('deviceId', deviceId);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await api.post('/cameras/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteMediaFile(id: string): Promise<void> {
    await api.delete(`/cameras/media/${id}`);
  }

  async downloadMediaFile(id: string): Promise<Blob> {
    const response = await api.get(`/cameras/media/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Statistics
  async getStats(): Promise<CameraStats> {
    const response = await api.get('/cameras/stats');
    return response.data;
  }

  // Bulk Operations
  async bulkSync(deviceIds: string[]): Promise<void> {
    await api.post('/cameras/bulk-sync', { deviceIds });
  }

  async bulkUpdateSettings(deviceIds: string[], settings: Partial<CameraSettings>): Promise<void> {
    await api.post('/cameras/bulk-update-settings', { deviceIds, settings });
  }

  async exportMediaData(filters?: any): Promise<Blob> {
    const response = await api.get('/cameras/export', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }
}

export const cameraManagementService = new CameraManagementService();
export default cameraManagementService;